import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useQuery as useConvexQuery } from "convex/react";
import { refs } from "../../convex/apiRefs";
import { decodeConvexDoc } from "../../convex/decodeConvexDoc";
import { diffDocuments } from "../../utility/jsonLineDiff";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
  MONGODB_DATASOURCE_TS,
} from "../../utility/keys";
import LoadingSpinner from "../../components/LoadingSpinner";
import { buildMongoQuickFilter } from "../../utility/compassMongoFilter";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Compass (#191) — the "Both" backend: a VS-Code-style side-by-side diff of
// each record's MongoDB document (left) vs its Convex-mirror twin (right).
//
// The MONGO page drives the record set (true offset paging, sorted by _id);
// for that page's ids we fetch the matching Convex rows in one call
// (compass:getManyByMongoId) and diff each pair. Records stack vertically in a
// single scroll container, so the two columns scroll together by construction.
// Red = present in Mongo only (removed); green = present in Convex only (added);
// a paired change shows old-on-left / new-on-right. A faithful mirror diffs to
// ZERO lines (the canonicalizer collapses $oid/$date so representation alone
// never lights up). READ ONLY — this is a drift inspector.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const SOURCE_TO_DB = { uat: "UAT", prod: "Warehouses" };

function idHex(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.$oid) return value.$oid;
  return String(value);
}

function sideStyle(kind) {
  if (kind === "del") return { backgroundColor: "rgba(220, 38, 38, 0.22)" };
  if (kind === "add") return { backgroundColor: "rgba(34, 197, 94, 0.22)" };
  if (kind === "empty") return { backgroundColor: "rgba(127, 127, 127, 0.06)" };
  return undefined;
}

function DiffLine({ no, text, kind }) {
  return (
    <div className="flex" style={sideStyle(kind)}>
      <span
        className="select-none text-right pr-2 opacity-50"
        style={{ width: "3em", flexShrink: 0 }}
      >
        {no ?? ""}
      </span>
      <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {text ?? ""}
      </span>
    </div>
  );
}

function RecordDiff({ block }) {
  const { label, id, rows, stats, convexDoc } = block;
  // line numbers per side
  let ln = 0;
  let rn = 0;
  const rendered = rows.map((r, i) => {
    const leftKind =
      r.type === "same" ? "same" : r.type === "del" || r.type === "change" ? "del" : "empty";
    const rightKind =
      r.type === "same" ? "same" : r.type === "add" || r.type === "change" ? "add" : "empty";
    if (r.left != null) ln++;
    if (r.right != null) rn++;
    return (
      <div key={i} className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <DiffLine no={r.left != null ? ln : null} text={r.left} kind={leftKind} />
        <div style={{ borderLeft: "1px solid rgba(127,127,127,0.3)" }}>
          <DiffLine no={r.right != null ? rn : null} text={r.right} kind={rightKind} />
        </div>
      </div>
    );
  });

  return (
    // No `overflow-hidden` here — it would create a clipping context that
    // defeats the header's `sticky top-0`. Without it, each record's header
    // sticks to the top of the scroll container while that record's JSON
    // scrolls under it, and is pushed up + replaced by the next record's
    // header as it arrives (later sticky header paints over the earlier one).
    <div className="mb-3 border-2 border-skin-base rounded">
      <div className="flex items-center gap-2 px-2 py-1 bg-skin-primary text-xs sticky top-0 z-10 border-b border-skin-base">
        <span className="font-medium truncate">{label}</span>
        <span className="opacity-60 truncate">{id}</span>
        <span className="ml-auto">
          {convexDoc === null ? (
            <span className="text-red font-medium">missing in Convex</span>
          ) : stats.identical ? (
            <span style={{ color: "#22c55e" }} className="font-medium">
              identical ✓
            </span>
          ) : (
            <span className="font-medium">
              <span style={{ color: "#22c55e" }}>+{stats.additions + stats.changes}</span>{" "}
              <span className="text-red">−{stats.deletions + stats.changes}</span>
            </span>
          )}
        </span>
      </div>
      <div className="font-mono text-[11px] leading-snug">{rendered}</div>
    </div>
  );
}

function CompassDiffBrowser({
  source,
  mongoCollection,
  convexTable,
  rawFilter,
  searchField,
  searchValue,
  pageSize,
}) {
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");

  const database = SOURCE_TO_DB[source] || "UAT";
  const filter = useMemo(
    () => buildMongoQuickFilter(rawFilter, searchField, searchValue),
    [rawFilter, searchField, searchValue]
  );

  useEffect(() => {
    setPage(1);
    setPageInput("1");
  }, [source, mongoCollection, searchField, searchValue, pageSize, rawFilter]);

  // Mongo page drives the record set.
  const { data, isFetching, error } = useQuery({
    queryKey: [
      "compassDiffMongo",
      database,
      mongoCollection,
      JSON.stringify(filter),
      page,
      pageSize,
    ],
    queryFn: async () => {
      const body = {
        method: "browseCollection",
        dataSource: MONGODB_DATASOURCE_TS,
        database,
        collection: mongoCollection,
        filter,
        sort: { _id: 1 },
        page,
        pageSize,
      };
      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        headers: {
          "x-api-key": MONGODB_AWSGW_APIKEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await response.json();
      return json?.message?.result ?? null;
    },
    keepPreviousData: true,
  });

  const docs = useMemo(() => data?.result ?? [], [data]);
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const mongoIds = useMemo(
    () => docs.map((d) => idHex(d._id, null)).filter(Boolean),
    [docs]
  );

  // Pair the page's ids with their Convex twins in one query.
  const convexRows = useConvexQuery(
    refs.compassGetManyByMongoId,
    mongoIds.length ? { source, table: convexTable, mongoIds } : "skip"
  );
  const convexPending = mongoIds.length > 0 && convexRows === undefined;

  const convexById = useMemo(() => {
    const map = new Map();
    if (Array.isArray(convexRows)) {
      for (const r of convexRows) map.set(r.mongoId, decodeConvexDoc(r.data));
    }
    return map;
  }, [convexRows]);

  const blocks = useMemo(() => {
    return docs.map((d) => {
      const id = idHex(d._id, "");
      const convexDoc = convexById.has(id) ? convexById.get(id) : null;
      const { rows, stats } = diffDocuments(d, convexDoc);
      const label =
        d.invoiceNo ?? d.Name_Full ?? d.username ?? d.location ?? d.id ?? id;
      return { id, label: String(label), rows, stats, convexDoc };
    });
  }, [docs, convexById]);

  const driftCount = blocks.filter(
    (b) => b.convexDoc === null || !b.stats.identical
  ).length;

  const go = (p) => {
    const clamped = Math.min(Math.max(1, p), totalPages);
    setPage(clamped);
    setPageInput(String(clamped));
  };

  const loading = (isFetching && docs.length === 0) || convexPending;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-3 text-xs pb-1 flex-wrap">
        <span className="font-medium">MongoDB ⟷ Convex diff</span>
        <span className="opacity-70">
          Mongo drives the page; each record paired with its Convex twin by _id.
        </span>
        {!loading && (
          <span className="ml-auto">
            {driftCount === 0 ? (
              <span style={{ color: "#22c55e" }} className="font-medium">
                all {blocks.length} in sync ✓
              </span>
            ) : (
              <span className="text-red font-medium">
                {driftCount} of {blocks.length} differ on this page
              </span>
            )}
          </span>
        )}
      </div>

      {/* No top padding: the sticky record headers stick flush to the scroll
          container's top edge (top padding would leave a gap content peeks
          through). Sides + bottom padding kept. */}
      <div className="flex-1 min-h-0 overflow-auto border-2 border-skin-base rounded px-2 pb-2">
        {error ? (
          <div className="text-red text-sm p-4">{String(error.message || error)}</div>
        ) : loading ? (
          <LoadingSpinner />
        ) : blocks.length === 0 ? (
          <div className="text-skin-link text-sm p-4 italic">No documents match.</div>
        ) : (
          blocks.map((b) => <RecordDiff key={b.id} block={b} />)
        )}
      </div>

      {/* Offset pagination — Mongo-driven */}
      <div className="flex items-center gap-2 pt-2 text-xs flex-wrap">
        <button className="px-2 py-1 border-2 border-skin-base rounded disabled:opacity-40" onClick={() => go(1)} disabled={page <= 1}>
          |◀ First
        </button>
        <button className="px-2 py-1 border-2 border-skin-base rounded disabled:opacity-40" onClick={() => go(page - 1)} disabled={page <= 1}>
          ◀ Prev
        </button>
        <span className="px-1">
          Page
          <input
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const n = parseInt(pageInput, 10);
                if (Number.isFinite(n)) go(n);
              }
            }}
            className="w-14 mx-1 px-1 py-0.5 text-black rounded text-center"
            aria-label="Jump to page"
          />
          of {totalPages.toLocaleString()}
        </span>
        <button className="px-2 py-1 border-2 border-skin-base rounded disabled:opacity-40" onClick={() => go(page + 1)} disabled={page >= totalPages}>
          Next ▶
        </button>
        <button className="px-2 py-1 border-2 border-skin-base rounded disabled:opacity-40" onClick={() => go(totalPages)} disabled={page >= totalPages}>
          Last ▶|
        </button>
        <span className="ml-auto text-skin-link">
          {totalCount.toLocaleString()} document{totalCount === 1 ? "" : "s"}
          {isFetching ? " · loading…" : ""}
        </span>
      </div>
    </div>
  );
}

export default CompassDiffBrowser;
